import superagent from 'superagent'
import { batch } from 'react-redux'
import { circlesSet } from './circle-actions'
import { linesSet } from './line-actions'
import { polygonsSet } from './polygon-actions'
import { tracksSet } from './track-actions'
import { routesSet } from './route-actions'
import { markersSet } from './marker-actions'
import { photosSet } from './photo-actions'
import { boundariesSet } from './boundary-actions'
import { setSyncCheckpoint, clearSyncCheckpoint } from './sync-checkpoint-actions'

// Guards against overlapping full syncs. The bootstrap call, the 2.5-min
// realtime-update interval, and post-import all call fetchChanges(); without this
// a large account's sweep (which outlasts 2.5 min) gets a fresh page-0 sweep
// stacked on top every interval, re-fetching the same shallow pages dozens of
// times and never finishing (WEB-7060).
let syncInFlight = false

// A page that fails transiently (deep-page timeout / 500) is retried in place
// with backoff instead of abandoning the whole sweep (which the interval would
// then restart from page 0). Deep OFFSET-pagination timeouts are the server half
// (API-3552); this keeps one failed page from throwing away all sweep progress.
const MAX_PAGE_RETRIES = 4
const RETRY_BASE_MS = 1000
// Cap a single page request so a hung connection can't hold syncInFlight forever.
const REQUEST_TIMEOUT_MS = 60000

export const setChangesLastCalled = () => {
  let now = new Date()
  return {
    type: 'CHANGES_LAST_CALLED_SET',
    payload: now.toISOString()
  }
}

const prune = (map) => {
  const arr = []
  map.forEach(obj => {
    if (obj.deleted) {
      return
    }
    arr.push(obj)
  })
  return arr
}

// Push the accumulated content to Redux in one batch. Reads (never mutates) the
// accumulation Maps via prune(), so the sweep can keep accumulating after a
// commit. Called both on completion and on failure, so partial progress from an
// interrupted sweep is retained (and the next sweep re-seeds from it).
const commitMeasures = (dispatch, measures) => {
  batch(() => {
    dispatch(linesSet(prune(measures.lines)))
    dispatch(circlesSet(prune(measures.circles)))
    dispatch(polygonsSet(prune(measures.polygons)))
    dispatch(tracksSet(prune(measures.tracks)))
    dispatch(routesSet(prune(measures.routes)))
    dispatch(markersSet(prune(measures.markers)))
    dispatch(photosSet(prune(measures.photos)))
    dispatch(boundariesSet(prune(measures.mapBoundaries)))
  })
}

// One seek-paged changes/get call. `cursor`/`cursorId` are the resume key: a
// sweep's first call passes the watermark as the cursor with no id, which the
// server reads as the start of a sweep (it still applies its replica-lag overlap
// through EffectiveFromDate), and each continuation passes the (nextCursor,
// nextCursorId) pair from the previous response. Sending a cursor at all is what
// selects seek paging over the legacy OFFSET mode, whose deep pages cost 8-18s
// apiece (API-3552). `date` stays the sweep's base watermark, used for asOfDate
// and the getDeleted rule.
const changesRequest = (cursor, cursorId, token, measures = {}, dispatch, date, attempt = 0) => {
  const typeMap = {
    1: 'tracks',
    2: 'routes',
    3: 'polygons',
    4: 'lines',
    5: 'circles'
  },
    getDeleted = date === '2018-01-01T00:00:00.000Z' ? false : true,
    // Position label for logs; the cursor pair replaces the old page number.
    at = cursorId != null ? `${cursor}#${cursorId}` : `${cursor}`

  return new Promise((resolve, reject) => {
    superagent
      .get(
        `${__API_URI_2__}/changes/get` +
        `?activityId=1` +
        `&asOfDate=${date}` +
        `&pageSize=100` +
        `&includeDeletedContent=${getDeleted}` +
        `&cursor=${encodeURIComponent(cursor)}` +
        (cursorId != null ? `&cursorId=${cursorId}` : '')
      )
      .timeout({ deadline: REQUEST_TIMEOUT_MS })
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token}`)
      .set('Platform', 'Web')
      .set('Version', `${__version__}`)
      .end((err, res) => {

        // ---- AUTH ERROR ----
        if (err?.status === 401) {
          console.warn('401 Unauthorized — logging out');
          dispatch({ type: 'LOGOUT' });
          reject(err);
          return;
        }

        // ---- OTHER ERRORS: retry this page with backoff before giving up ----
        if (err) {
          if (attempt < MAX_PAGE_RETRIES) {
            const backoff = RETRY_BASE_MS * Math.pow(2, attempt);
            console.warn(
              `Changes API error at cursor ${at} (attempt ${attempt + 1}/${MAX_PAGE_RETRIES + 1}), retrying in ${backoff}ms:`,
              err?.status || err?.message || err
            );
            setTimeout(() => {
              resolve(changesRequest(cursor, cursorId, token, measures, dispatch, date, attempt + 1));
            }, backoff);
            return;
          }
          console.error('Changes API error (retries exhausted) at cursor', at, err);
          reject(err);
          return;
        }

        const {
          measurements = [],
          markers = [],
          photos = [],
          mapBoundaries = [],
          nextCursor = null,
          nextCursorId = null,
          hasMoreChanges
        } = res?.body || {};

        const isEmptyResponse =
          measurements.length === 0 &&
          markers.length === 0 &&
          photos.length === 0 &&
          mapBoundaries.length === 0;

        // Rows are applied before the continuation decision. In seek mode the
        // final page is normally NON-empty with hasMoreChanges false, so the old
        // "an empty page means done" early return would have discarded it.
        markers.forEach(marker => {
          if (marker?.id) {
            measures.markers.set(marker.id, marker);
          }
        });

        photos.forEach(photo => {
          if (photo?.id) {
            measures.photos.set(photo.id, photo);
          }
        });

        mapBoundaries.forEach(boundary => {
          if (boundary?.id) {
            boundary.measureType = 'boundary';
            measures.mapBoundaries.set(boundary.id, boundary);
          }
        });

        measurements.forEach(measure => {
          const mapKey = typeMap?.[measure?.measurementTypeId];
          if (mapKey && measures[mapKey]) {
            measures[mapKey].set(measure.id, measure);
          }
        });

        // Continue only while the server reports more AND handed back a resume
        // key. hasMoreChanges is the seek-mode signal (API-3552); if a server
        // predating it answers without the field, fall back to the old "an empty
        // page ends the sweep" rule rather than stopping after the first page.
        const serverHasMore =
          hasMoreChanges === undefined ? !isEmptyResponse : hasMoreChanges;

        if (serverHasMore && nextCursor != null) {
          // Checkpoint AFTER this page's rows are in `measures`, so the resume
          // point can never run ahead of the content the sweep has taken.
          dispatch(setSyncCheckpoint(nextCursor, nextCursorId));
          resolve(
            changesRequest(
              nextCursor,
              nextCursorId,
              token,
              measures,
              dispatch,
              date
            )
          );
          return;
        }

        if (serverHasMore) {
          // More changes but nowhere to resume from would re-request the same
          // key forever; end the sweep and let the next tick start over.
          console.warn(
            'changes/get reported more changes but returned no nextCursor; ending sweep at',
            at
          );
        }

        // End of the sweep — commit everything accumulated.
        commitMeasures(dispatch, measures);
        resolve(measures);
      });
  });

}

const fetchPhoto = (photoId, token) => {
  $.ajax({
    method: 'GET',
    url: `${__API_URI__}/photo/get/${photoId}`,
    headers: {
      Authorization: `Bearer ${token}`,
      Platform: 'Web',
      Version: `${__version__}`
    }
  }).done(res => {
    s.photo = res
  })
}

export const fetchChanges = () => (dispatch, getState) => {
  // Don't stack a second page-0 sweep on top of a running one (WEB-7060).
  if (syncInFlight) {
    return Promise.resolve()
  }

  const storeChanges = getState().changesLastCalled,
    changesDate = storeChanges ? storeChanges : '2018-01-01T00:00:00.000Z',
    checkpoint = getState().syncCheckpoint,
    token = getState().auth.token,
    lines = new Map(),
    circles = new Map(),
    polygons = new Map(),
    tracks = new Map(),
    routes = new Map(),
    markers = new Map(),
    photos = new Map(),
    mapBoundaries = new Map(),
    measures = {
      tracks,
      routes,
      polygons,
      lines,
      circles,
      markers,
      photos,
      mapBoundaries
    }
  getState().lines.forEach(line => lines.set(line.id, line))
  getState().circles.forEach(circle => circles.set(circle.id, circle))
  getState().polygons.forEach(polygon => polygons.set(polygon.id, polygon))
  getState().tracks.forEach(track => tracks.set(track.id, track))
  ;(getState().routes || []).forEach(route => routes.set(route.id, route))
  getState().markers.forEach(marker => markers.set(marker.id, marker))
  getState().photos.forEach(photo => photos.set(photo.id, photo))
  getState().boundaries.forEach(boundary => mapBoundaries.set(boundary.id, boundary))

  // Resume from the mid-sweep checkpoint when the previous sweep was interrupted,
  // otherwise begin at the watermark (WEB-7061). A fresh sweep still sends the
  // watermark AS the cursor — with no cursorId — because sending a cursor is what
  // selects seek paging, and the server treats a cursor without an id as the start
  // of a sweep rather than a mid-sweep continuation.
  const startCursor = checkpoint && checkpoint.cursor ? checkpoint.cursor : changesDate,
    startCursorId = checkpoint && checkpoint.cursorId != null ? checkpoint.cursorId : null

  syncInFlight = true
  return changesRequest(startCursor, startCursorId, token, measures, dispatch, changesDate)
    .then(() => {
      // Sweep completed: drop the resume point, then advance the sync watermark.
      // The watermark still only moves on success, so an interrupted sweep retries
      // instead of skipping ahead and permanently missing content — the difference
      // is that it now retries from the checkpoint rather than from the watermark.
      dispatch(clearSyncCheckpoint())
      dispatch(setChangesLastCalled())
    })
    .catch(err => {
      // Non-401 failure (page timeout/500 after retries). Commit whatever the
      // sweep accumulated so the partial progress is durable and visible, and leave
      // the watermark unadvanced so the next call retries rather than skipping
      // ahead. The checkpoint is deliberately left in place: it is what the next
      // sweep resumes from, so the already-fetched pages are not bought again
      // (WEB-7061). A 401 clears it via the LOGOUT case in the reducer.
      if (err?.status !== 401) {
        commitMeasures(dispatch, measures)
      }
      console.error('fetchChanges sync did not complete:', err?.status || err?.message || err)
    })
    .finally(() => {
      syncInFlight = false
    })
}
